import { useEffect, useRef, useState } from "react";
import api from "../lib/api";
import { Flame, Loader2, RefreshCw } from "lucide-react";

// Shows a friendly full-screen "we'll be right back" overlay when the backend
// is unreachable (network error / 502 / 503 / 504) — e.g. a paused deployment —
// instead of a broken white screen. Auto-recovers when the API comes back.
export default function ConnectionGuard() {
  const [down, setDown] = useState(false);
  const [checking, setChecking] = useState(false);
  const failsRef = useRef(0);
  const downRef = useRef(false);

  const check = async () => {
    setChecking(true);
    try {
      await api.get("/health", { timeout: 8000 });
      failsRef.current = 0;
      downRef.current = false;
      setDown(false);
      return true;
    } catch (e) {
      const status = e.response?.status;
      const outage = !e.response || [502, 503, 504].includes(status);
      if (outage) {
        failsRef.current += 1;
        if (failsRef.current >= 2) { downRef.current = true; setDown(true); }
      } else {
        // A 4xx means the server is actually up.
        failsRef.current = 0;
        downRef.current = false;
        setDown(false);
      }
      return false;
    } finally {
      setChecking(false);
    }
  };

  useEffect(() => {
    let stop = false;
    let timer;
    const loop = async () => {
      if (stop) return;
      await check();
      if (stop) return;
      timer = setTimeout(loop, downRef.current ? 6000 : 25000);
    };
    loop();
    return () => { stop = true; clearTimeout(timer); };
  }, []);

  if (!down) return null;

  return (
    <div
      data-testid="connection-guard"
      className="fixed inset-0 z-[9999] bg-[#020202] flex items-center justify-center px-6"
      style={{ backgroundImage: "radial-gradient(circle at 50% 30%, rgba(0,229,255,0.08), transparent 60%)" }}
    >
      <div className="max-w-md w-full text-center animate-in fade-in zoom-in-95 duration-500">
        <div className="flex items-center justify-center gap-2 mb-8">
          <Flame className="text-[#00E5FF]" size={26} strokeWidth={2.5} />
          <span className="font-heading text-2xl uppercase tracking-tighter text-white">Kink<span className="text-[#00E5FF]">Vault</span></span>
        </div>
        <div className="relative mx-auto mb-7 w-16 h-16">
          <span className="absolute inset-0 rounded-full border-2 border-[#00E5FF]/20" />
          <Loader2 className="absolute inset-0 m-auto text-[#00E5FF] animate-spin" size={40} />
        </div>
        <h1 className="font-heading text-3xl uppercase tracking-tight text-white mb-3">We'll be right back</h1>
        <p className="text-[#A1A1AA] text-sm leading-relaxed mb-8">
          The vault is taking a quick breather. Your account and everything you've uploaded are safe — this page will reconnect on its own the moment we're back.
        </p>
        <button
          data-testid="connection-retry"
          onClick={check}
          disabled={checking}
          className="inline-flex items-center gap-2 bg-[#00E5FF] text-black font-heading uppercase tracking-[0.2em] text-xs px-6 py-3 rounded-xl hover:bg-[#00c9e0] transition-colors disabled:opacity-50 shadow-[0_0_18px_rgba(0,229,255,0.35)]"
        >
          {checking ? <Loader2 className="animate-spin" size={15} /> : <RefreshCw size={15} />} Try again
        </button>
      </div>
    </div>
  );
}
